// AppVersionConfiguration.jsx

import React, { useEffect, useRef, useState } from 'react'
import { withTranslation } from 'react-i18next'
import { useMutation, gql, useQuery } from '@apollo/client'
import { validateFunc } from '../../../constraints/constraints'
import { getVersions, updateAppVersion } from '../../../apollo'
import useStyles from '../styles'
import useGlobalStyles from '../../../utils/globalStyles'
import { Box, Typography, Input, Button } from '@mui/material'

const UPDATE_APP_VERSIONS = gql`
  ${updateAppVersion}
`

function AppVersionConfiguration(props) {
  const formRef = useRef()

  const [customerVersion, setCustomerVersion] = useState(
    props.customerVersion || { android: '', ios: '' }
  )

  const [riderVersion, setRiderVersion] = useState(
    props.riderVersion || { android: '', ios: '' }
  )
  const [restaurantVersion, setRestaurantVersion] = useState(
    props.restaurantVersion || { android: '', ios: '' }
  )

  const [customerVersionError, setCustomerVersionError] = useState({
    android: null,
    ios: null
  })
  const [riderVersionError, setRiderVersionError] = useState({
    android: null,
    ios: null
  })
  const [restaurantVersionError, setRestaurantVersionError] = useState({
    android: null,
    ios: null
  })

  // API
  const [mutate, { loading }] = useMutation(UPDATE_APP_VERSIONS)

  const onBlur = (setter, field, state) => {
    setter(prev => ({
      ...prev,
      [field]: !validateFunc({ [field]: state }, field)
    }))
  }

  const validateInput = () => {
    const fields = ['android', 'ios']
    const apps = ['customer', 'rider', 'restaurant']
    let isValid = true

    apps.forEach(app => {
      fields.forEach(field => {
        const result = !validateFunc(
          {
            [`${app}Version${
              field.charAt(0).toUpperCase() + field.slice(1)
            }`]: formRef.current[`input-${app}Version-${field}`].value
          },
          `${app}Version${field.charAt(0).toUpperCase() + field.slice(1)}`
        )

        if (app === 'customer')
          setCustomerVersionError(prev => ({ ...prev, [field]: result }))
        if (app === 'rider')
          setRiderVersionError(prev => ({ ...prev, [field]: result }))
        if (app === 'restaurant')
          setRestaurantVersionError(prev => ({ ...prev, [field]: result }))
        isValid = isValid && result
      })
    })

    return isValid
  }

  const classes = useStyles()
  const globalClasses = useGlobalStyles()

  return (
    <Box container className={classes.container}>
      <Box className={classes.flexRow}>
        <Box item className={classes.heading}>
          <Typography variant="h6" className={classes.text}>
            App Version
          </Typography>
        </Box>
      </Box>

      <Box className={classes.form}>
        <form ref={formRef}>
          {['customer', 'rider', 'restaurant'].map(app => (
            <Box key={app} className={globalClasses.flexRow}>
              <Box>
                <Typography className={classes.labelText}>
                  {app.charAt(0).toUpperCase() + app.slice(1)} Android Version
                </Typography>
                <Input
                  style={{ marginTop: -1 }}
                  id={`input-${app}Version-android`}
                  name={`input-${app}Version-android`}
                  placeholder="e.g 1.0.0"
                  defaultValue={
                    app === 'customer'
                      ? customerVersion.android
                      : app === 'rider'
                      ? riderVersion.android
                      : restaurantVersion.android
                  }
                  onBlur={event =>
                    onBlur(
                      app === 'customer'
                        ? setCustomerVersionError
                        : app === 'rider'
                        ? setRiderVersionError
                        : setRestaurantVersionError,
                      'android',
                      event.target.value
                    )
                  }
                  disableUnderline
                  className={[
                    globalClasses.input,
                    (app === 'customer'
                      ? customerVersionError.android
                      : app === 'rider'
                      ? riderVersionError.android
                      : restaurantVersionError.android) === false
                      ? globalClasses.inputError
                      : (app === 'customer'
                          ? customerVersionError.android
                          : app === 'rider'
                          ? riderVersionError.android
                          : restaurantVersionError.android) === true
                      ? globalClasses.inputSuccess
                      : ''
                  ]}
                />
              </Box>
              <Box>
                <Typography className={classes.labelText}>
                  {app.charAt(0).toUpperCase() + app.slice(1)} iOS Version
                </Typography>
                <Input
                  style={{ marginTop: -1 }}
                  id={`input-${app}Version-ios`}
                  name={`input-${app}Version-ios`}
                  placeholder="e.g 1.0.0"
                  defaultValue={
                    app === 'customer'
                      ? customerVersion.ios
                      : app === 'rider'
                      ? riderVersion.ios
                      : restaurantVersion.ios
                  }
                  onBlur={event =>
                    onBlur(
                      app === 'customer'
                        ? setCustomerVersionError
                        : app === 'rider'
                        ? setRiderVersionError
                        : setRestaurantVersionError,
                      'ios',
                      event.target.value
                    )
                  }
                  disableUnderline
                  className={[
                    globalClasses.input,
                    (app === 'customer'
                      ? customerVersionError.ios
                      : app === 'rider'
                      ? riderVersionError.ios
                      : restaurantVersionError.ios) === false
                      ? globalClasses.inputError
                      : (app === 'customer'
                          ? customerVersionError.ios
                          : app === 'rider'
                          ? riderVersionError.ios
                          : restaurantVersionError.ios) === true
                      ? globalClasses.inputSuccess
                      : ''
                  ]}
                />
              </Box>
            </Box>
          ))}
          <Box>
            <Button
              className={globalClasses.button}
              disabled={loading}
              onClick={e => {
                e.preventDefault()
                if (validateInput() && !loading) {
                  mutate({
                    variables: {
                      customerAppVersion: {
                        android:
                          formRef.current['input-customerVersion-android']
                            .value,
                        ios: formRef.current['input-customerVersion-ios'].value
                      },
                      riderAppVersion: {
                        android:
                          formRef.current['input-riderVersion-android'].value,
                        ios: formRef.current['input-riderVersion-ios'].value
                      },
                      restaurantAppVersion: {
                        android:
                          formRef.current['input-restaurantVersion-android']
                            .value,
                        ios:
                          formRef.current['input-restaurantVersion-ios'].value
                      }
                    }
                  })
                }
              }}>
              SAVE
            </Button>
          </Box>
        </form>
      </Box>
    </Box>
  )
}

export default withTranslation()(AppVersionConfiguration)
